/**
 * Quiz Editor
 * Editor component for Quiz content type
 */

import * as React from 'react';
import {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
    Button,
    Input,
    Label,
    Textarea,
    cn,
    Switch,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    Badge,
} from '@ai-content-hub/content-kit';
import {
    MCQEdit,
    TextEdit,
    type MCQElementData,
    type TextElementData,
    mcqElementSchema,
    textElementSchema,
    getQuestionText,
} from '@ai-content-hub/content-elements';
import type { QuizData, QuizItem, QuizSection, QuizConfig, QuizElementType } from './schema';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp, FileQuestion, Type } from 'lucide-react';
import { generateId, useLocale } from '@ai-content-hub/content-core';

export interface QuizEditProps {
    /** Quiz data - passed by content-core */
    value: QuizData;
    /** Callback when data changes */
    onChange: (value: QuizData) => void;
    /** Config (optional) */
    config?: QuizConfig;
    /** Whether editing is disabled */
    disabled?: boolean;
    /** Validation errors keyed by field path */
    errors?: Record<string, string>;
    /** Additional class name */
    className?: string;
}

export const QuizEdit: React.FC<QuizEditProps> = ({
    value: data,
    onChange,
    config,
    disabled = false,
    errors,
    className,
}) => {
    const { t } = useLocale({ config: config?.i18n });

    const elementTypes = React.useMemo(
        () =>
            [
                { type: 'mcq' as const, label: t('multipleChoice'), icon: FileQuestion },
                { type: 'text' as const, label: t('textBlock'), icon: Type },
            ] as const,
        [t]
    );
    const [expandedItems, setExpandedItems] = React.useState<Set<string>>(new Set());

    // Get items (either flat or from sections)
    const items = data?.items || [];

    // Update data
    const updateData = (updates: Partial<QuizData>) => {
        onChange({
            ...data,
            ...updates,
        });
    };

    // Toggle item expansion
    const toggleExpanded = (itemId: string) => {
        setExpandedItems((prev) => {
            const next = new Set(prev);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            return next;
        });
    };

    // Add new item
    const addItem = (type: QuizElementType) => {
        if (disabled) return;

        let defaultData: MCQElementData | TextElementData;
        let gradeable = false;
        let points = 0;

        switch (type) {
            case 'mcq':
                defaultData = mcqElementSchema.defaultData as MCQElementData;
                gradeable = true;
                points = 1;
                break;
            case 'text':
                defaultData = textElementSchema.defaultData as TextElementData;
                break;
            default:
                defaultData = textElementSchema.defaultData as TextElementData;
        }

        const newItem: QuizItem = {
            id: generateId(),
            type,
            data: defaultData,
            gradeable,
            points,
        };

        updateData({
            items: [...items, newItem],
        });

        // Auto-expand new item
        setExpandedItems((prev) => new Set(prev).add(newItem.id));
    };

    // Remove item
    const removeItem = (itemId: string) => {
        if (disabled) return;
        updateData({
            items: items.filter((item) => item.id !== itemId),
        });
    };

    // Update item
    const updateItem = (itemId: string, itemData: MCQElementData | TextElementData) => {
        if (disabled) return;
        updateData({
            items: items.map((item) => (item.id === itemId ? { ...item, data: itemData } : item)),
        });
    };

    // Update item settings
    const updateItemSettings = (itemId: string, settings: Partial<QuizItem>) => {
        if (disabled) return;
        updateData({
            items: items.map((item) => (item.id === itemId ? { ...item, ...settings } : item)),
        });
    };

    // Move item up
    const moveUp = (index: number) => {
        if (disabled || index === 0) return;
        const newItems = [...items];
        [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
        updateData({ items: newItems });
    };

    // Move item down
    const moveDown = (index: number) => {
        if (disabled || index === items.length - 1) return;
        const newItems = [...items];
        [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
        updateData({ items: newItems });
    };

    // Get item type label
    const getTypeLabel = (type: QuizElementType) => {
        return elementTypes.find((et) => et.type === type)?.label || type;
    };

    // Get item preview text
    const getItemPreview = (item: QuizItem) => {
        switch (item.type) {
            case 'mcq': {
                const q = getQuestionText(item.data as MCQElementData);
                return q || t('untitledQuestion');
            }
            case 'text': {
                const txt = (item.data as TextElementData)?.text?.slice(0, 50);
                return txt || t('emptyText');
            }
            default:
                return t('unknownItem');
        }
    };

    // Render item editor based on type
    const renderItemEditor = (item: QuizItem) => {
        switch (item.type) {
            case 'mcq':
                return (
                    <MCQEdit
                        value={item.data as MCQElementData}
                        onChange={(newData) => updateItem(item.id, newData)}
                        disabled={disabled}
                        config={config}
                    />
                );
            case 'text':
                return (
                    <TextEdit
                        value={item.data as TextElementData}
                        onChange={(newData) => updateItem(item.id, newData)}
                        disabled={disabled}
                        config={config}
                    />
                );
            default:
                return <div className="text-muted-foreground">{t('unknownElementType')}</div>;
        }
    };

    return (
        <div className={cn('quiz-edit space-y-6', className)}>
            {/* Quiz Details */}
            <Card className="quiz-edit-details-card">
                <CardHeader className="quiz-edit-details-header">
                    <CardTitle className="quiz-edit-details-title">{t('quizDetails')}</CardTitle>
                </CardHeader>
                <CardContent className="quiz-edit-details-body space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="title">{t('title')}</Label>
                        <Input
                            id="title"
                            value={data?.title || ''}
                            onChange={(e) => updateData({ title: e.target.value })}
                            placeholder={t('quizTitlePlaceholder')}
                            disabled={disabled}
                            className={cn(errors?.title && 'border-destructive')}
                        />
                        {errors?.title && (
                            <p className="quiz-edit-error text-sm text-destructive">{errors.title}</p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="description">{t('description')}</Label>
                        <Input
                            id="description"
                            value={data?.description || ''}
                            onChange={(e) => updateData({ description: e.target.value })}
                            placeholder={t('quizDescriptionPlaceholder')}
                            disabled={disabled}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="instructions">{t('instructions')}</Label>
                        <Textarea
                            id="instructions"
                            value={data?.instructions || ''}
                            onChange={(e) => updateData({ instructions: e.target.value })}
                            placeholder={t('quizInstructionsPlaceholder')}
                            disabled={disabled}
                            rows={3}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Quiz Items */}
            <div className="quiz-edit-items space-y-4">
                <div className="quiz-edit-items-header flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                        {t('quizItems')} ({items.length})
                    </h3>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button size="sm" disabled={disabled}>
                                <Plus className="h-4 w-4 mr-2" />
                                {t('addItem')}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[180px]">
                            {elementTypes.map(({ type, label, icon: Icon }) => (
                                <DropdownMenuItem key={type} onClick={() => addItem(type)}>
                                    <Icon className="h-4 w-4 mr-2" />
                                    {label}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {items.length === 0 ? (
                    <Card className="quiz-edit-empty border-dashed">
                        <CardContent className="quiz-edit-empty-body py-12 text-center">
                            <p className="text-muted-foreground mb-4">
                                {t('noItemsYet')}
                            </p>
                            {errors?.items && (
                                <p className="quiz-edit-error text-sm text-destructive mb-4">{errors.items}</p>
                            )}
                            <div className="flex justify-center gap-2">
                                {elementTypes.map(({ type, label, icon: Icon }) => (
                                    <Button
                                        key={type}
                                        variant="outline"
                                        onClick={() => addItem(type)}
                                        disabled={disabled}
                                    >
                                        <Icon className="h-4 w-4 mr-2" />
                                        {t('add')} {label}
                                    </Button>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="quiz-edit-item-list space-y-3">
                        {items.map((item, index) => {
                            const isExpanded = expandedItems.has(item.id);
                            return (
                                <Card key={item.id} className="quiz-edit-item overflow-hidden">
                                    {/* Item Header */}
                                    <div
                                        className="quiz-edit-item-header flex items-center gap-2 p-3 bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors"
                                        onClick={() => toggleExpanded(item.id)}
                                    >
                                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                                        <Badge variant="outline" className="font-medium text-primary border-primary/30 bg-primary/10">
                                            {getTypeLabel(item.type)}
                                        </Badge>
                                        <span className="flex-1 truncate text-sm text-muted-foreground">
                                            {getItemPreview(item)}
                                        </span>
                                        {item.gradeable && (
                                            <Badge variant="outline" className="text-xs text-success border-success/30 bg-success/10">
                                                {item.points} {t('points')}
                                            </Badge>
                                        )}
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    moveUp(index);
                                                }}
                                                disabled={disabled || index === 0}
                                            >
                                                <ChevronUp className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    moveDown(index);
                                                }}
                                                disabled={disabled || index === items.length - 1}
                                            >
                                                <ChevronDown className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-destructive hover:text-destructive"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeItem(item.id);
                                                }}
                                                disabled={disabled}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Item Editor */}
                                    {isExpanded && (
                                        <CardContent className="quiz-edit-item-body pt-4 space-y-4">
                                            {/* Item settings for gradeable items */}
                                            {item.type === 'mcq' && (
                                                <div className="flex items-center gap-4 pb-4 border-b">
                                                    <div className="flex items-center gap-2">
                                                        <Switch
                                                            checked={item.gradeable !== false}
                                                            onCheckedChange={(checked) =>
                                                                updateItemSettings(item.id, {
                                                                    gradeable: checked,
                                                                })
                                                            }
                                                            disabled={disabled}
                                                        />
                                                        <Label className="text-sm">{t('gradeable')}</Label>
                                                    </div>
                                                    {item.gradeable !== false && (
                                                        <div className="flex items-center gap-2">
                                                            <Label className="text-sm">{t('pointsLabel')}</Label>
                                                            <Input
                                                                type="number"
                                                                min={0}
                                                                value={item.points || 1}
                                                                onChange={(e) =>
                                                                    updateItemSettings(item.id, {
                                                                        points: parseInt(e.target.value) || 1,
                                                                    })
                                                                }
                                                                className="w-20"
                                                                disabled={disabled}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Element editor */}
                                            {renderItemEditor(item)}
                                        </CardContent>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuizEdit;
